import type {
  IProductRepository,
  ProductFilters,
  ProductSearchSuggestionsResult,
} from '../../../domain/repositories/IProductRepository';
import { PaginatedResponse, Product } from '../../../domain/entities/Product';

export class GetProductsUseCase {
  constructor(private readonly productRepository: IProductRepository) {}

  async getAll(filters?: ProductFilters): Promise<Product[]> {
    return this.productRepository.findAll(filters);
  }

  async getById(id: string): Promise<Product> {
    const product = await this.productRepository.findById(id);
    if (!product) {
      throw new Error('Product not found');
    }
    return product;
  }

  async getByCategory(
    categoryId: string,
    filters?: ProductFilters
  ): Promise<PaginatedResponse<Product>> {
    return this.productRepository.findByCategory(categoryId, filters);
  }

  async getByShop(
    shopId: string,
    filters?: ProductFilters
  ): Promise<Product[]> {
    return this.productRepository.findByShop(shopId, filters);
  }

  async getFeatured(limit: number = 10): Promise<Product[]> {
    return this.productRepository.findFeatured(limit);
  }

  async search(query: string, filters?: ProductFilters): Promise<Product[]> {
    if (!query.trim()) {
      return [];
    }
    return this.productRepository.search(query, filters);
  }

  async searchSuggestions(
    query: string,
    filters?: ProductFilters
  ): Promise<ProductSearchSuggestionsResult> {
    if (!query.trim()) {
      return { products: [], suggestions: [], source: 'none' };
    }

    if (!this.productRepository.searchSuggestions) {
      const products = await this.productRepository.search(query, filters);
      return {
        products,
        suggestions: products.map((product) => product.name).slice(0, 10),
        source: 'postgres',
      };
    }

    return this.productRepository.searchSuggestions(query, filters);
  }

  async getRecommendations(
    userId: string,
    limit: number = 10
  ): Promise<Product[]> {
    // TODO: Implement recommendation logic based on user behavior, purchase history, etc.
    // For now, return featured products
    return this.productRepository.findFeatured(limit);
  }

  async getRelated(productId: string, limit: number = 5): Promise<Product[]> {
    const product = await this.getById(productId);

    // Get products from the same categories
    const relatedProducts = await this.productRepository.findByCategory(
      product.category.id ?? '', // Use first category for simplicity
      {
        limit: limit + 1, // Get one extra to exclude the current product
        status: 'A',
      }
    );

    // Filter out the current product and limit results
    return relatedProducts.results
      .filter((p) => p.id !== productId)
      .slice(0, limit);
  }
}
